<template>
  <modal background>
    <validable-form @submit="submit">
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ video ? "Edycja filmu" : "Nowy film" }}
          </p>
          <button
            class="delete"
            type="button"
            aria-label="close"
            @click="cancel"
          ></button>
        </header>

        <section class="modal-card-body content">
          <div class="columns">
            <div class="column">
              <validation-provider rules="required" v-slot="{ errors }">
                <video-source-picker
                  ref="videoPath"
                  @search="search"
                  @change-page="changeSourcesPage"
                  v-model="source"
                  :sources="sources"
                  :pageable="sourcesPageable"
                  :error="errors[0]"
                  :state="actions.sources"
                />
              </validation-provider>
            </div>
            <div class="column">
              <span class="label">Miniaturka</span>
              <validation-provider rules="required" v-slot="{ errors }">
                <thumbnail-uploader
                  v-model="thumbnail"
                  :uploadFunction="uploadThumbnail"
                  :error="errors[0]"
                  :url="thumbnailUrl"
                />
              </validation-provider>

              <div class="field">
                <label class="label">Tytuł</label>
                <control-input
                  type="text"
                  rules="required"
                  icon-left="fa-tag"
                  v-model.trim="title"
                  placeholder="Aktualności..."
                />
              </div>

              <div class="field">
                <label class="label">Data publikacji</label>
                <control-date-time-picker
                  ref="createdDate"
                  v-model="createdDate"
                  icon-left="fa-calendar-alt"
                  placeholder="Data publikacji..."
                  alt-format="j F Y, H:i"
                  date-format="Z"
                  :dates="[isDateAvailable]"
                  time
                />
              </div>
            </div>
          </div>
          <div>
            <span class="label">Tagi</span>
            <validation-provider rules="required" v-slot="{ errors }">
              <video-tag-selector
                :tags="tags"
                v-model="selectedTags"
                :error="errors[0]"
              />
            </validation-provider>
          </div>
        </section>

        <footer class="modal-card-foot">
          <button
            class="button is-success"
            :class="{ 'is-loading': submitting }"
            :disabled="submitting"
          >
            <icon :name="video ? 'fa-save' : 'fa-plus'" />
            <span>{{ video ? "Zapisz" : "Dodaj" }}</span>
          </button>

          <button class="button" @click="cancel">Anuluj</button>
        </footer>
      </div>
    </validable-form>
  </modal>
</template>

<script>
import dayjs from "dayjs";

import { mapState } from "vuex";

import { throttle } from "%utils/functions";

import { omit } from "%utils/object";

import { FETCH_VIDEO_SOURCES, UPLOAD_THUMBNAIL, FETCH_TAGS } from ":actions";

// Fixme: these should be imported with { } braces, however it doesn't work
import WithDispatcherProgress from "%mixins/WithDispatcherProgress";
import WithServerValidation from "%mixins/WithServerValidation";

import ControlDateTimePicker from "%admin/components/global/ControlDateTimePicker";

import { Modal } from "%modals";

import {
  Icon,
  VideoSourcePicker,
  ThumbnailUploader,
  ControlInput,
  VideoTagSelector,
  ValidableForm
} from "%helpers";

import { dropIfNotChanged } from "%utils/form";

export default {
  name: "video-modal",

  components: {
    Modal,
    Icon,
    VideoSourcePicker,
    ThumbnailUploader,
    ControlInput,
    VideoTagSelector,
    ValidableForm,
    ControlDateTimePicker
  },

  mixins: [
    WithDispatcherProgress,

    WithServerValidation({
      "videoPath:UNIQUE":
        "Wybrany plik został już wykorzystany w innym materiale",
      "createdDate:PAST_OR_PRESENT": "Data publikacji powinna być datą przeszłą"
    })
  ],

  props: {
    onSubmit: Function,
    video: Object
  },

  data() {
    return {
      source: "",
      thumbnail: "",
      title: "",
      selectedTags: [],
      submitting: false,
      thumbnailUrl: process.env.VUE_APP_THUMBNAIL_MIN_URL,
      createdDate: dayjs().toISOString()
    };
  },

  methods: {
    submit() {
      this.submitting = true;
      this.onSubmit &&
        this.onSubmit({
          title: dropIfNotChanged(this.title, this.video, "title"),
          thumbnailPath: dropIfNotChanged(
            this.thumbnail,
            this.video,
            "thumbnailPath"
          ),
          videoPath: dropIfNotChanged(
            this.source,
            this.video,
            "videoSources",
            x => x[0].replace(/\.[^/.]+$/, "")
          ),
          tags: dropIfNotChanged(
            this.selectedTags.map(id => ({ id })),
            this.video,
            "tags"
          ),
          createdDate: dropIfNotChanged(
            this.createdDate,
            this.video,
            "createdDate"
          )
        })
          .then(() => this.cancel())
          .catch(this.handleValidationError)
          .finally(() => (this.submitting = false));
    },

    cancel() {
      this.$emit("hide");
    },

    search: throttle(function(query) {
      this.$store.dispatch(FETCH_VIDEO_SOURCES, {
        ...this.sourcesPageable,
        page: 1,
        query
      });
    }, 400),

    changeSourcesPage(page) {
      this.dispatchWithProgress("sources", FETCH_VIDEO_SOURCES, {
        ...this.sourcesPageable,
        page
      });
    },

    uploadThumbnail(image) {
      return this.$store.dispatch(UPLOAD_THUMBNAIL, { image });
    },

    isDateAvailable(date) {
      return dayjs().isAfter(date);
    }
  },

  computed: mapState({
    sources: state => state.videos.videoSources.content,
    sourcesPageable: state => omit(state.videos.videoSources, "content"),
    tags: state => state.videos.tags
  }),

  beforeMount() {
    if (this.video) {
      this.title = this.video.title;
      this.selectedTags = this.video.tags.map(x => x.id);
      this.thumbnail = this.video.thumbnailPath;
      this.createdDate = this.video.createdDate;

      if (this.video.videoSources.length > 0) {
        this.source = this.video.videoSources[0].replace(/\.[^/.]+$/, "");
      }
    }

    this.dispatchWithProgress("sources", FETCH_VIDEO_SOURCES, {
      page: 1,
      size: 4
    });
    this.$store.dispatch(FETCH_TAGS);
  }
};
</script>

<style lang="scss" scoped>
.modal-card {
  @include desktop {
    min-width: 60rem;
  }
}
</style>
